import { Colors } from "../constants/colors";
import { OvenMediaContext, OvenMediaRecordSubtitles, OvenMediaSubtitlesStatus } from "../interfaces/context";
import { OvenMediaWhisperContainerState } from "../interfaces/subtitles";

// What the Records tab shows of a recording's automatic transcript, and what the cost modal says.
// Display only: the statuses are the middleware's, the figures are the whisper-kalyzee image's.

export const SUBTITLES_STATUS_LABELS: Record<OvenMediaSubtitlesStatus, string> = {
  [OvenMediaSubtitlesStatus.QUEUED]: "en file" /* TRANSLATION */,
  [OvenMediaSubtitlesStatus.WAITING_IDLE]: "en attente" /* TRANSLATION */,
  [OvenMediaSubtitlesStatus.TRANSCRIBING]: "en cours" /* TRANSLATION */,
  [OvenMediaSubtitlesStatus.SILENT]: "silencieux" /* TRANSLATION */,
  [OvenMediaSubtitlesStatus.TRANSCRIBED]: "transcrit" /* TRANSLATION */,
  [OvenMediaSubtitlesStatus.DELIVERED]: "livré" /* TRANSLATION */,
  [OvenMediaSubtitlesStatus.ERROR]: "erreur" /* TRANSLATION */,
};

export const getSubtitlesStatusLabel = (status: OvenMediaSubtitlesStatus | string): string =>
  SUBTITLES_STATUS_LABELS[status as OvenMediaSubtitlesStatus] ?? status;

export const getSubtitlesStatusColor = (status: OvenMediaSubtitlesStatus | string): string => {
  switch (status) {
    case OvenMediaSubtitlesStatus.DELIVERED:
      return Colors.getMainGreen();
    case OvenMediaSubtitlesStatus.TRANSCRIBED:
      return Colors.getMainGreen(0.6);
    case OvenMediaSubtitlesStatus.TRANSCRIBING:
      return Colors.getBlueCornflower();
    case OvenMediaSubtitlesStatus.QUEUED:
    case OvenMediaSubtitlesStatus.WAITING_IDLE:
      return Colors.getTreePoppy();
    case OvenMediaSubtitlesStatus.ERROR:
      return Colors.getTorchRed();
    default:
      return Colors.getMainIndianKhaki();
  }
};

const formatClock = (date: Date) => date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

/** The override as the chip says it: « forcé jusqu'à 14:30 (même en cours de flux) », or undefined once it has lapsed. */
export const getSubtitlesOverrideText = (subtitles: Pick<OvenMediaRecordSubtitles, "override">): string | undefined => {
  if (!subtitles.override) return undefined;
  const until = new Date(subtitles.override.until);
  if (Number.isNaN(until.getTime()) || until.getTime() < Date.now()) return undefined;
  return `forcé jusqu'à ${formatClock(until)}${subtitles.override.busy ? " (même pendant un flux)" : ""}` /* TRANSLATION */;
};

/** The chip of the « Sous-titres » column: its text, its colour, and what the hover adds. */
export const getSubtitlesChip = (subtitles: OvenMediaRecordSubtitles): { text: string; color: string; hover?: string } => {
  const text =
    subtitles.status === OvenMediaSubtitlesStatus.TRANSCRIBING && subtitles.piecesTotal
      ? `${subtitles.piecesDone ?? 0}/${subtitles.piecesTotal}`
      : getSubtitlesStatusLabel(subtitles.status);
  const parts: string[] = [];
  if (subtitles.status === OvenMediaSubtitlesStatus.WAITING_IDLE && subtitles.waitingFor) parts.push(subtitles.waitingFor);
  if (subtitles.status === OvenMediaSubtitlesStatus.ERROR && subtitles.error) parts.push(subtitles.error);
  const override = getSubtitlesOverrideText(subtitles);
  if (override) parts.push(override);
  return { text, color: getSubtitlesStatusColor(subtitles.status), hover: parts.length ? parts.join(" — ") : undefined };
};

/** Why a record has no subtitles state: the server does not transcribe, or this record was never queued. */
export const getSubtitlesAbsentMessage = (context?: OvenMediaContext): string =>
  context?.subtitles && !context.subtitles.configured ? "non configuré sur ce serveur" /* TRANSLATION */ : "non planifié" /* TRANSLATION */;

/** Whether the server can transcribe at all (configured, and the container is a real Whisper). */
export const isSubtitlesAvailable = (context?: OvenMediaContext): boolean =>
  !!context?.subtitles?.configured && context.subtitles.installed !== false;

/** A transcription that already ended for this build: « maintenant » must redo it from scratch. */
export const isSubtitlesTerminal = (status: OvenMediaSubtitlesStatus | string | undefined): boolean =>
  status === OvenMediaSubtitlesStatus.TRANSCRIBED || status === OvenMediaSubtitlesStatus.DELIVERED || status === OvenMediaSubtitlesStatus.SILENT;

/** The filter of the Records page: each option is the list of statuses it asks the server for. */
export const SUBTITLES_FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: [OvenMediaSubtitlesStatus.QUEUED, OvenMediaSubtitlesStatus.WAITING_IDLE].join(","), label: "en attente" /* TRANSLATION */ },
  { value: OvenMediaSubtitlesStatus.TRANSCRIBING, label: "en cours" /* TRANSLATION */ },
  { value: OvenMediaSubtitlesStatus.TRANSCRIBED, label: "transcrits, à livrer" /* TRANSLATION */ },
  { value: OvenMediaSubtitlesStatus.DELIVERED, label: "livrés" /* TRANSLATION */ },
  { value: OvenMediaSubtitlesStatus.SILENT, label: "silencieux" /* TRANSLATION */ },
  { value: OvenMediaSubtitlesStatus.ERROR, label: "en erreur" /* TRANSLATION */ },
  { value: "none", label: "sans sous-titres" /* TRANSLATION */ },
];

export const CONTAINER_STATE_LABELS: Record<OvenMediaWhisperContainerState, string> = {
  "always-on": "service externe, toujours actif" /* TRANSLATION */,
  absent: "conteneur introuvable" /* TRANSLATION */,
  "stand-in": "conteneur factice (pas de GPU)" /* TRANSLATION */,
  stopped: "arrêté" /* TRANSLATION */,
  starting: "démarrage (chargement du modèle)" /* TRANSLATION */,
  running: "en marche" /* TRANSLATION */,
};

export const getContainerStateColor = (state: OvenMediaWhisperContainerState): string => {
  switch (state) {
    case "running":
      return Colors.getMainGreen();
    case "starting":
      return Colors.getBlueCornflower();
    case "stopped":
    case "always-on":
      return Colors.getMainIndianKhaki();
    default:
      return Colors.getTorchRed();
  }
};

/**
 * What a run costs the host, per model — measured on the whisper-kalyzee image (large-v3, fp16) and
 * on the bureau's 8 GB card. Static on purpose: a live `nvidia-smi` is not reachable through the
 * middleware. The model name comes from the server's context.
 */
export interface SubtitlesCost {
  vramPeakGb: number;
  ramPerPieceGb: number;
  modelLoadSeconds: string;
  firstAnswerMinutes: number;
}

export const SUBTITLES_COST_BY_MODEL: Record<string, SubtitlesCost> = {
  "large-v3": { vramPeakGb: 5.3, ramPerPieceGb: 0.5, modelLoadSeconds: "6 à 17", firstAnswerMinutes: 10 },
};

export const getSubtitlesCost = (model?: string): { model: string; cost: SubtitlesCost; measured: boolean } => {
  if (model && SUBTITLES_COST_BY_MODEL[model]) return { model, cost: SUBTITLES_COST_BY_MODEL[model], measured: true };
  return { model: model ?? "large-v3", cost: SUBTITLES_COST_BY_MODEL["large-v3"], measured: false };
};
